
export const noop = (): void => {};

/**
 * Returns true if the given value is not null or undefined.
 *
 * @param {T} value value to test
 * @returns {boolean} true if value is not null or undefiend
 */
export function hasValue<T>(value: T): value is NonNullable<T> {
    return typeof value !== 'undefined' && value !== null;
}

/**
 * Returns true if the given string is not undefined, not null, has a length
 * greater than one, and contains characters other than whitespace.
 *
 * @param {string} str the string to test
 * @returns {boolean} true if value is string with at least one non-whitespace character
 */
export function isStringNotBlank(str: string | undefined | null): boolean {
    return hasValue(str) && str.trim().length > 0;
}

/**
 * Returns the given value or a default if the value is null
 * or undefined.
 *
 * @param {T} value value to test
 * @param {T} defaultValue value to return if test value is null or undefined
 * @returns {T} given value or default
 */
export function getValue<T>(value: T | undefined | null, defaultValue: T): T {
    return hasValue(value) ? value : defaultValue;
}

/**
 * Returns the given string or a default if the given string does not have
 * at least one non-whitespace character.
 *
 * @param {string} value string to test
 * @param {string} defaultValue default string
 * @returns {string} given value or default value if given value is invalid
 */
export function getStringValue(value: string | undefined | null, defaultValue: string): string {
    return isStringNotBlank(value) ? value : defaultValue;
}

/**
 * Returns the given string as an integer or a default if the given string cannot
 * be parsed to a base 10 integer.
 *
 * @param {string} value string to be parsed
 * @param {number} defaultValue default value
 * @returns {number} the value parsed to an int or the default value
 */
export function getInt(value: string | undefined | null, defaultValue: number | null): number {
    if (hasValue(value)) {
        const numberValue = parseInt(value, 10);
        return isNaN(numberValue) ? defaultValue : numberValue;
    }
    return defaultValue;
}

export function isPopulatedArray<T = unknown>(value: T | T[]): value is T[] {
    if (Array.isArray(value) && value.length > 0) {
        for (const item of value) {
            if (hasValue(item)) {
                return true;
            }
        }
    }

    return false;
}

export function doesObjectHaveValues(obj: any): boolean {
    if (obj === null || typeof obj !== 'object') {
        return false;
    }
    for (const value of Object.values(obj)) {
        if (hasValue(value)) {
            if (Array.isArray(value)) {
                if (value.length > 0) {
                    return true;
                }
            }
            else {
                return true;
            }
        }
    }
    return false;
}

export function addUnique<T>(arr1: T[], arr2: T[]): T[] {
    const arr = Array.isArray(arr1) ? arr1 : [];
    if (isPopulatedArray(arr2)) {
        arr2.forEach(item => {
            if (!arr.includes(item)) {
                arr.push(item);
            }
        });
    }
    return arr;
}

export function intersection<T>(arr1: T[], arr2: T[]): T[] {
    const checkedArr1 = hasValue(arr1) ? arr1 : [];
    const checkedArr2 = hasValue(arr2) ? arr2 : [];
    return checkedArr1.filter(item => checkedArr2.includes(item));
}

export function createObjectFromArray<T, V>(
    arr: T[],
    keyFunc: (arg0: T) => string,
    valueFunc: (arg0: T) => V,
    requireValue = false
): {[key: string]: V} {
    if (isPopulatedArray(arr)) {
        return arr.reduce((acc, item) => {
            const obj = {...acc};
            const key = keyFunc(item);
            if (requireValue) {
                if (hasValue(key)) {
                    const value = valueFunc(item);
                    if (hasValue(value)) {
                        obj[keyFunc(item)] = valueFunc(item);
                    }
                }
            }
            else {
                obj[key] = valueFunc(item);
            }
            return obj;
        }, {});
    }
    return {};
}

export function arrayToObject<T>(arr: T[], keyFunc: (arg0: T) => string): {[key: string]: T} {
    return createObjectFromArray(arr, keyFunc, (item: T) => item);
}

/**
 * Checks to see if two arrays contain the same elements. This DOES NOT check if they are in the same order.
 * @param arr1
 * @param arr2
 *
 * True if the arrays contain the same elements, false otherwise.
 */
export function arraysContainSameElements(arr1: any[], arr2: any[]): boolean {
    // First check if lengths match
    if (!Array.isArray(arr1) || !Array.isArray(arr2) || arr1.length !== arr2.length) {
        return false;
    }

    const sortedArr1 = arr1.concat().sort();
    const sortedArr2 = arr2.concat().sort();

    for (let i = 0; i < sortedArr1.length; i++) {
        if (sortedArr1[i] !== sortedArr2[i]) {
            return false;
        }
    }
    return true;
}

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- `any` is actually required in this case */
export const cast = <T>(thing: any): T => thing as unknown as T;
